import React, { useState } from 'react';
import { Stack, Typography, MenuItem, FormControl, TextField, InputAdornment, Switch, Box } from '@mui/material';
import { useCampaignForm } from 'hooks';
import { BillingMethod, CampaignFields } from 'types';
import { Controller } from 'react-hook-form';
import { Card } from 'components/Card';
import { InfoMessage } from 'components/InfoMessage';
import { InfoTooltip } from 'components/InfoTooltip';
import { infoMessagesTop, infoMessagesBottom, texts, MIN_PRICE_ENGAGEMENT, MIN_PRICE_CLICK } from 'shared';
import { numberInputStyling } from 'shared/ui';

export const KPIs = () => {
  const {
    control,
    watch,
    getFieldControls,
    setValue,
    trigger,
    formState: { errors },
    onFieldChange,
  } = useCampaignForm();
  const [shouldShowInput, setShouldShowInput] = useState(false);
  const price = watch(CampaignFields.maxUnitPrice);
  const isInputAndDataShown = !!price || shouldShowInput;

  const billingMethodSelectedValue = watch(CampaignFields.billingMethod);
  const { minPrice, cardTitle, inputLabel, selectHelper } = texts[billingMethodSelectedValue];
  const isEngagementBilling = billingMethodSelectedValue === BillingMethod.ENGAGEMENT;

  const shouldShowWarning =
    !!price &&
    !errors.maxUnitPrice &&
    price >= 1 &&
    ((isEngagementBilling && price < MIN_PRICE_ENGAGEMENT) || (!isEngagementBilling && price < MIN_PRICE_CLICK));

  return (
    <Stack gap={6}>
      <Typography variant="subtitle1">Preismodell wählen</Typography>
      <Stack width={{ xs: 1, lg: 2 / 3 }}>
        <FormControl fullWidth>
          <Controller
            control={control}
            name={CampaignFields.billingMethod}
            rules={{
              required: 'Fülle bitte das Feld aus',
            }}
            render={({ field: { onChange, value } }) => (
              <TextField
                select
                fullWidth
                label="Preismodell"
                variant="outlined"
                value={value}
                onChange={(e) => {
                  onFieldChange(CampaignFields.billingMethod);
                  return onChange(e);
                }}
                size="small"
                helperText={selectHelper}
              >
                <MenuItem value={BillingMethod.ENGAGEMENT}>
                  Preis pro Engagement -{' '}
                  <Typography sx={{ fontWeight: 'bold' }} component={'b'}>
                    Empfohlen
                  </Typography>
                </MenuItem>
                <MenuItem value={BillingMethod.CLICK}>Preis pro Klick</MenuItem>
              </TextField>
            )}
          />
        </FormControl>
      </Stack>
      {infoMessagesTop[billingMethodSelectedValue]?.map(({ title, message }, index) => (
        <InfoMessage title={title} message={message} key={index} />
      ))}
      <Card sx={{ width: { xs: 1, lg: 2 / 3 } }}>
        <Stack gap={6}>
          <Stack direction="row" justifyContent="start" alignItems="center">
            <Switch
              id="kpi-switch"
              checked={isInputAndDataShown}
              onChange={() =>
                setShouldShowInput((prev) => {
                  if (isInputAndDataShown) {
                    setValue(CampaignFields.maxUnitPrice, null);
                    onFieldChange(CampaignFields.maxUnitPrice);
                    return false;
                  }
                  return !prev;
                })
              }
            />
            <Typography variant="subtitle1">{cardTitle}</Typography>
            <Box sx={{ display: 'inline-block', transform: 'translateY(1px)', ml: 2 }}>
              <InfoTooltip
                text={
                  <Typography variant="body2">
                    Alloy sucht immer den günstigsten Preis für dich – unabhängig davon, ob du einen Maximalpreis
                    angibst oder nicht.
                  </Typography>
                }
              />
            </Box>
          </Stack>
          {isInputAndDataShown && (
            <Stack gap={6}>
              <>
                <Stack width={{ xs: 1, md: 1 / 2 }}>
                  <TextField
                    fullWidth
                    label={inputLabel}
                    variant="outlined"
                    size="small"
                    type="number"
                    sx={numberInputStyling}
                    slotProps={{
                      input: { startAdornment: <InputAdornment position="start">CHF</InputAdornment> },
                    }}
                    helperText={errors.maxUnitPrice?.message}
                    error={!!errors.maxUnitPrice?.message}
                    {...getFieldControls(CampaignFields.maxUnitPrice, {
                      min: { value: 1, message: 'Überprüfe bitte deine Angabe und gib ein geeignetes Max. Preis ein' },
                    })}
                    onChange={async (e) => {
                      if (!shouldShowInput) {
                        setShouldShowInput(true);
                      }
                      setValue(CampaignFields.maxUnitPrice, e.target.value ? Number(e.target.value) : null);
                      const isValid = await trigger(CampaignFields.maxUnitPrice);
                      isValid && onFieldChange(CampaignFields.maxUnitPrice);
                    }}
                  />
                  {shouldShowWarning && (
                    <Typography variant="body3" color="warning.dark" pt={1} pl={4}>
                      <b>Hinweis: </b>
                      {minPrice}
                    </Typography>
                  )}
                </Stack>
                {infoMessagesBottom[billingMethodSelectedValue]?.map(({ title, message }, index) => (
                  <InfoMessage title={title} message={message} key={index} fullWidth />
                ))}
              </>
            </Stack>
          )}
        </Stack>
      </Card>
    </Stack>
  );
};
